import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Ticket,
  AlertTriangle,
  ArrowRight,
  Clock,
  CheckCircle2,
  ExternalLink,
  User,
  MessageSquare,
} from "lucide-react";
import { TicketDiscussionDialog } from "@/components/integrations/ticket-discussion-dialog";

interface TicketSummarySlideProps {
  psaCompanyId?: string;
  customerName: string;
  reviewPeriod?: string;
  recommendations?: any[];
}

interface TicketMetrics {
  total: number;
  open: number;
  closed: number;
  avgResolutionDays: number;
  priorityBreakdown: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  categoryBreakdown: { category: string; count: number }[];
  topIssues: { summary: string; count: number }[];
}

interface IntegrationConfig {
  serverUrl?: string;
  companyId?: string;
}

// Closed statuses that MSPs commonly use in their PSA systems
const CLOSED_STATUSES = [
  "closed", "resolved", "completed", "cancelled", "canceled", "done",
  "fixed", "finished", "archived", "rejected", "duplicate",
];

// Check if a status indicates a closed ticket
const isClosedStatus = (status?: string): boolean => {
  if (!status) return false;
  const normalizedStatus = status.toLowerCase().trim();
  return CLOSED_STATUSES.some(closedStatus => 
    normalizedStatus.includes(closedStatus) || closedStatus.includes(normalizedStatus)
  );
};

// Get status badge styling based on status
const getStatusBadge = (status?: string) => {
  if (!status) return <Badge variant="outline">Unknown</Badge>;
  const normalizedStatus = status.toLowerCase();
  
  if (isClosedStatus(status)) {
    return <Badge variant="secondary" className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">{status}</Badge>;
  }
  if (normalizedStatus.includes("new") || normalizedStatus.includes("open")) {
    return <Badge variant="default" className="bg-blue-600">{status}</Badge>;
  }
  if (normalizedStatus.includes("progress") || normalizedStatus.includes("working") || normalizedStatus.includes("assigned")) {
    return <Badge variant="default" className="bg-orange-500">{status}</Badge>;
  }
  if (normalizedStatus.includes("waiting") || normalizedStatus.includes("hold") || normalizedStatus.includes("pending")) {
    return <Badge variant="outline" className="border-yellow-500 text-yellow-700">{status}</Badge>;
  }
  return <Badge variant="outline">{status}</Badge>;
};

// Get priority badge styling
const getPriorityBadge = (priority?: string) => {
  if (!priority) return null;
  const normalizedPriority = priority.toLowerCase();
  
  if (normalizedPriority.includes("critical") || normalizedPriority.includes("emergency")) {
    return <Badge variant="destructive">{priority}</Badge>;
  }
  if (normalizedPriority.includes("high") || normalizedPriority.includes("priority 1") || normalizedPriority.includes("urgent")) {
    return <Badge variant="default" className="bg-orange-500">{priority}</Badge>;
  }
  if (normalizedPriority.includes("medium") || normalizedPriority.includes("normal") || normalizedPriority.includes("priority 3")) {
    return <Badge variant="secondary">{priority}</Badge>;
  }
  return <Badge variant="outline">{priority}</Badge>;
};

// Construct ConnectWise ticket URL
const getTicketUrl = (provider: string | null, serverUrl: string | undefined, instanceUrl: string | undefined, ticketId: string): string | null => {
  if (!ticketId) return null;

  if (provider === "halopsa" && instanceUrl) {
    return `${instanceUrl}/tickets?id=${ticketId}`;
  }

  if (provider === "connectwise" && serverUrl) {
    const baseUrl = serverUrl.replace('/v4_6_release/apis/3.0', '');
    return `${baseUrl}/v4_6_release/services/system_io/Service/fv_sr100_request.rails?service_recid=${ticketId}`;
  }

  return null;
};

// Map ticket types/categories to recommendation categories
const TICKET_TO_RECOMMENDATION_MAP: Record<string, string[]> = {
  "network": ["networking", "infrastructure", "connectivity"],
  "security": ["security", "compliance", "mfa", "firewall"],
  "hardware": ["hardware", "workstation", "server", "asset"],
  "software": ["software", "application", "license"],
  "email": ["email", "microsoft", "m365", "office"],
  "backup": ["backup", "disaster recovery", "business continuity"],
  "password": ["security", "mfa", "identity"],
  "printer": ["hardware", "peripheral"],
  "vpn": ["networking", "remote access", "security"],
  "phone": ["telecommunications", "voip"],
};

// Donut chart component for priority breakdown
function DonutChart({ data }: { data: { label: string; value: number; color: string }[] }) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  if (total === 0) return <div className="text-center text-muted-foreground text-sm">No data</div>;
  
  let cumulativePercent = 0;
  const segments = data.filter(d => d.value > 0).map((item) => {
    const percent = (item.value / total) * 100;
    const startPercent = cumulativePercent;
    cumulativePercent += percent;
    return { ...item, percent, startPercent };
  });

  // Create conic gradient
  const gradientStops = segments.map((seg) => 
    `${seg.color} ${seg.startPercent}% ${seg.startPercent + seg.percent}%`
  ).join(', ');

  return (
    <div className="flex items-center gap-4">
      <div 
        className="w-24 h-24 rounded-full relative"
        style={{ 
          background: `conic-gradient(${gradientStops})`,
        }}
      >
        <div className="absolute inset-3 bg-background rounded-full flex items-center justify-center">
          <span className="text-lg font-bold">{total}</span>
        </div>
      </div>
      <div className="space-y-1">
        {segments.map((seg, idx) => (
          <div key={idx} className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: seg.color }} />
            <span>{seg.label}: {seg.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function QbrTicketSummarySlide({
  psaCompanyId,
  customerName,
  reviewPeriod,
  recommendations = [],
}: TicketSummarySlideProps) {
  // State for ticket discussion dialog
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [showDiscussionDialog, setShowDiscussionDialog] = useState(false);

  // Check if a PSA integration is active (use status endpoint accessible to all users)
  const { data: integrations = [] } = useQuery<any[]>({
    queryKey: ["/api/integrations/status"],
  });

  const connectWiseIntegration = integrations.find(
    (i: any) => i.provider === "connectwise" && i.status === "active"
  );

  const haloPsaIntegration = integrations.find(
    (i: any) => i.provider === "halopsa" && i.status === "active"
  );

  const asioIntegration = integrations.find(
    (i: any) => i.provider === "connectwise_asio" && i.status === "active"
  );

  const activePsaProvider = connectWiseIntegration ? "connectwise" : haloPsaIntegration ? "halopsa" : asioIntegration ? "connectwise_asio" : null;

  // Extract serverUrl from integration status (included for ticket linking)
  const serverUrl = connectWiseIntegration?.serverUrl;
  const instanceUrl = haloPsaIntegration?.instanceUrl;

  // Fetch tickets for this customer from whichever PSA is active
  const { data: tickets = [], isLoading } = useQuery<any[]>({
    queryKey: ["/api/integrations/tickets", activePsaProvider, psaCompanyId, "qbr"],
    queryFn: async () => {
      if (!psaCompanyId || !activePsaProvider) return [];

      let url: string;
      if (activePsaProvider === "connectwise") {
        url = `/api/integrations/connectwise/tickets?companyId=${psaCompanyId}&pageSize=500`;
      } else if (activePsaProvider === "halopsa") {
        url = `/api/integrations/halopsa/tickets?clientId=${psaCompanyId}&pageSize=500`;
      } else {
        url = `/api/integrations/connectwise-asio/tickets?companyId=${psaCompanyId}&pageSize=500`;
      }

      const response = await fetch(url);
      if (!response.ok) throw new Error("Failed to fetch tickets");
      return response.json();
    },
    enabled: !!activePsaProvider && !!psaCompanyId,
    staleTime: 5 * 60 * 1000,
  });

  // Calculate review period date range
  const getReviewPeriodDates = () => {
    const now = new Date();
    let startDate: Date;
    let endDate: Date = now;

    if (reviewPeriod) {
      const quarterMatch = reviewPeriod.match(/Q(\d)\s+(\d{4})/i);
      const monthMatch = reviewPeriod.match(/(\w+)\s+(\d{4})/i);
      const halfMatch = reviewPeriod.match(/H(\d)\s+(\d{4})/i);

      if (quarterMatch) {
        const quarter = parseInt(quarterMatch[1]);
        const year = parseInt(quarterMatch[2]);
        const startMonth = (quarter - 1) * 3;
        const endMonth = startMonth + 2;
        startDate = new Date(year, startMonth, 1);
        endDate = new Date(year, endMonth + 1, 0, 23, 59, 59, 999);
      } else if (halfMatch) {
        const half = parseInt(halfMatch[1]);
        const year = parseInt(halfMatch[2]);
        startDate = new Date(year, (half - 1) * 6, 1);
        endDate = new Date(year, half * 6, 0, 23, 59, 59, 999);
      } else if (monthMatch) {
        const monthNames = ["january", "february", "march", "april", "may", "june",
          "july", "august", "september", "october", "november", "december"];
        const monthIndex = monthNames.indexOf(monthMatch[1].toLowerCase());
        const year = parseInt(monthMatch[2]);
        if (monthIndex >= 0) {
          startDate = new Date(year, monthIndex, 1);
          endDate = new Date(year, monthIndex + 1, 0, 23, 59, 59, 999);
        } else {
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        }
      } else {
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
      }
    } else {
      startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
    }
    return { startDate, endDate };
  };

  const { startDate, endDate } = getReviewPeriodDates();
  
  const periodTickets = tickets.filter((ticket) => {
    const ticketDateStr = ticket.dateEntered || ticket.lastUpdated;
    if (!ticketDateStr) return false;
    const ticketDate = new Date(ticketDateStr);
    return ticketDate >= startDate && ticketDate <= endDate;
  });

  // Calculate metrics
  const calculateMetrics = (): TicketMetrics => {
    const open = periodTickets.filter((t) => !isClosedStatus(t.status)).length;
    const closed = periodTickets.filter((t) => isClosedStatus(t.status)).length;

    const priorityBreakdown = {
      critical: periodTickets.filter((t) =>
        t.priority?.toLowerCase().includes("critical") || t.priority?.toLowerCase().includes("emergency")
      ).length,
      high: periodTickets.filter((t) =>
        t.priority?.toLowerCase().includes("high") || t.priority?.toLowerCase().includes("priority 1")
      ).length,
      medium: periodTickets.filter((t) =>
        t.priority?.toLowerCase().includes("medium") || t.priority?.toLowerCase().includes("normal") || t.priority?.toLowerCase().includes("priority 3")
      ).length,
      low: periodTickets.filter((t) =>
        t.priority?.toLowerCase().includes("low") || t.priority?.toLowerCase().includes("priority 4")
      ).length,
    };

    // Category breakdown by board/type
    const categoryMap = new Map<string, number>();
    periodTickets.forEach((ticket) => {
      const category = ticket.boardName || ticket.type || "Other";
      categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
    });
    const categoryBreakdown = Array.from(categoryMap.entries())
      .map(([category, count]) => ({ category, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);

    // Top recurring issues
    const issueMap = new Map<string, number>();
    periodTickets.forEach((ticket) => {
      const summary = ticket.summary?.toLowerCase() || "";
      const keywords = ["password", "email", "network", "printer", "vpn", "slow", 
        "error", "access", "login", "outlook", "teams", "phone", "backup", "security"];
      const matchedKeyword = keywords.find(k => summary.includes(k)) || "other";
      issueMap.set(matchedKeyword, (issueMap.get(matchedKeyword) || 0) + 1);
    });
    const topIssues = Array.from(issueMap.entries())
      .map(([summary, count]) => ({ summary: summary.charAt(0).toUpperCase() + summary.slice(1), count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Calculate average resolution time
    let totalResolutionDays = 0;
    let resolvedCount = 0;
    periodTickets.forEach((ticket) => {
      const enteredDateStr = ticket.dateEntered || ticket.lastUpdated;
      if (ticket.closedDate && enteredDateStr) {
        const entered = new Date(enteredDateStr);
        const closed = new Date(ticket.closedDate);
        const days = (closed.getTime() - entered.getTime()) / (1000 * 60 * 60 * 24);
        if (days >= 0 && days < 365) {
          totalResolutionDays += days;
          resolvedCount++;
        }
      }
    });

    return {
      total: periodTickets.length,
      open,
      closed,
      avgResolutionDays: resolvedCount > 0 ? Math.round(totalResolutionDays / resolvedCount * 10) / 10 : 0,
      priorityBreakdown,
      categoryBreakdown,
      topIssues,
    };
  };

  // Get open tickets for the table display
  const openTickets = periodTickets
    .filter((t) => !isClosedStatus(t.status))
    .sort((a, b) => {
      // Sort by priority (critical first), then by date
      const priorityOrder: Record<string, number> = { critical: 0, emergency: 0, high: 1, urgent: 1, medium: 2, normal: 2, low: 3 };
      const aPriority = Object.entries(priorityOrder).find(([k]) => a.priority?.toLowerCase().includes(k))?.[1] ?? 2;
      const bPriority = Object.entries(priorityOrder).find(([k]) => b.priority?.toLowerCase().includes(k))?.[1] ?? 2;
      if (aPriority !== bPriority) return aPriority - bPriority;
      return new Date(b.dateEntered || 0).getTime() - new Date(a.dateEntered || 0).getTime();
    })
    .slice(0, 8); // Show top 8 open tickets

  // Find recommendations that relate to ticket categories
  const findRelatedRecommendations = (metrics: TicketMetrics) => {
    const related: { issue: string; recommendation: any; relevance: string }[] = [];
    metrics.topIssues.forEach((issue) => {
      const issueKey = issue.summary.toLowerCase();
      const relatedCategories = TICKET_TO_RECOMMENDATION_MAP[issueKey] || [];
      recommendations.forEach((rec) => {
        const recText = `${rec.title} ${rec.description}`.toLowerCase();
        const isRelated = relatedCategories.some(cat => recText.includes(cat));
        if (isRelated && !related.find(r => r.recommendation.id === rec.id)) {
          related.push({
            issue: issue.summary,
            recommendation: rec,
            relevance: `${issue.count} tickets related to ${issue.summary.toLowerCase()}`,
          });
        }
      });
    });
    return related.slice(0, 3);
  };

  // If no PSA integration or no PSA mapping
  if (!activePsaProvider || !psaCompanyId) {
    return (
      <div className="h-full flex flex-col">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-foreground flex items-center justify-center gap-2">
            <Ticket className="h-6 w-6" />
            Service Summary
          </h2>
          <p className="text-muted-foreground mt-1">{reviewPeriod || "Review Period"}</p>
        </div>
        <Card className="flex-1 flex items-center justify-center">
          <CardContent className="text-center py-8">
            <Ticket className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {!activePsaProvider ? "PSA integration not configured" : "Customer not linked to PSA"}
            </p>
            <p className="text-sm text-muted-foreground mt-2">Connect your PSA to display service ticket metrics</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-foreground flex items-center justify-center gap-2">
            <Ticket className="h-6 w-6" />
            Service Summary
          </h2>
        </div>
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[1, 2, 3, 4].map((i) => (<Skeleton key={i} className="h-24" />))}
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-48" />
          <Skeleton className="h-48" />
        </div>
      </div>
    );
  }

  const metrics = calculateMetrics();
  const relatedRecommendations = findRelatedRecommendations(metrics);
  const resolutionRate = metrics.total > 0 ? Math.round((metrics.closed / metrics.total) * 100) : 0;

  // Prepare chart data
  const priorityChartData = [
    { label: "Critical", value: metrics.priorityBreakdown.critical, color: "#ef4444" },
    { label: "High", value: metrics.priorityBreakdown.high, color: "#f97316" },
    { label: "Medium", value: metrics.priorityBreakdown.medium, color: "#6b7280" },
    { label: "Low", value: metrics.priorityBreakdown.low, color: "#d1d5db" },
  ].filter(d => d.value > 0);

  return (
    <div className="h-full flex flex-col overflow-auto">
      {/* Header */}
      <div className="text-center mb-4">
        <h2 className="text-2xl font-bold text-foreground flex items-center justify-center gap-2">
          <Ticket className="h-6 w-6" />
          Service Summary
        </h2>
        <p className="text-muted-foreground mt-1">{reviewPeriod || "Review Period"} • {customerName}</p>
      </div>

      {/* Key Metrics Row */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/50 dark:to-blue-800/50 border-blue-200 dark:border-blue-700">
          <CardContent className="p-3 text-center">
            <div className="text-3xl font-bold text-blue-600 dark:text-blue-200">{metrics.total}</div>
            <div className="text-xs text-blue-700 dark:text-blue-100 font-medium">Total Tickets</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/50 dark:to-amber-800/50 border-amber-200 dark:border-amber-700">
          <CardContent className="p-3 text-center">
            <div className="text-3xl font-bold text-amber-600 dark:text-amber-200">{metrics.open}</div>
            <div className="text-xs text-amber-700 dark:text-amber-100 font-medium">Open</div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/50 dark:to-green-800/50 border-green-200 dark:border-green-700">
          <CardContent className="p-3 text-center">
            <div className="text-3xl font-bold text-green-600 dark:text-green-200">{metrics.closed}</div>
            <div className="text-xs text-green-700 dark:text-green-100 font-medium">Resolved</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <div className="text-3xl font-bold">{metrics.avgResolutionDays}</div>
            <div className="text-xs text-muted-foreground font-medium">Avg Days to Resolve</div>
          </CardContent>
        </Card>
      </div>

      {/* Resolution Rate + Priority Chart Row */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {/* Resolution Rate */}
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium flex items-center gap-1">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Resolution Rate
              </span>
              <span className="text-lg font-bold">{resolutionRate}%</span>
            </div>
            <Progress value={resolutionRate} className="h-3" />
            <div className="flex justify-between mt-2 text-xs text-muted-foreground">
              <span>{metrics.closed} resolved</span>
              <span>{metrics.open} open</span>
            </div>
          </CardContent>
        </Card>

        {/* Priority Donut Chart */}
        <Card>
          <CardHeader className="pb-1 pt-2 px-3">
            <CardTitle className="text-sm font-medium flex items-center gap-1">
              <AlertTriangle className="h-4 w-4" />
              Priority Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <DonutChart data={priorityChartData} />
          </CardContent>
        </Card>
      </div>

      {/* Open Tickets Table */}
      {openTickets.length > 0 && (
        <Card className="flex-1 min-h-0">
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="text-sm font-medium flex items-center justify-between">
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4 text-orange-500" />
                Open Tickets Requiring Attention ({metrics.open})
              </span>
              {metrics.open > 8 && (
                <Badge variant="outline" className="text-xs">Showing top 8</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px] py-3 text-sm">ID</TableHead>
                  <TableHead className="py-3 text-sm min-w-[200px]">Summary</TableHead>
                  <TableHead className="w-[160px] py-3 text-sm">Status</TableHead>
                  <TableHead className="w-[180px] py-3 text-sm">Priority</TableHead>
                  <TableHead className="w-[120px] py-3 text-sm">Assigned</TableHead>
                  <TableHead className="w-[70px] py-3 text-sm text-right">Age</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {openTickets.map((ticket) => {
                  const ticketUrl = getTicketUrl(activePsaProvider, serverUrl, instanceUrl, ticket.id);
                  const ticketAge = ticket.dateEntered 
                    ? Math.floor((Date.now() - new Date(ticket.dateEntered).getTime()) / (1000 * 60 * 60 * 24))
                    : null;
                  return (
                    <TableRow key={ticket.id}>
                      <TableCell className="py-3 font-mono text-sm">
                        {ticketUrl ? (
                          <a href={ticketUrl} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-500 hover:text-blue-400 hover:underline font-semibold">
                            #{ticket.id}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <span>#{ticket.id}</span>
                        )}
                      </TableCell>
                      <TableCell className="py-3 text-sm">
                        <button
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setShowDiscussionDialog(true);
                          }}
                          className="text-left w-full group"
                        >
                          <span className="line-clamp-1 group-hover:text-blue-500 group-hover:underline flex items-center gap-1" title={ticket.summary}>
                            {ticket.summary}
                            <MessageSquare className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                          </span>
                        </button>
                      </TableCell>
                      <TableCell className="py-3 whitespace-nowrap">{getStatusBadge(ticket.status)}</TableCell>
                      <TableCell className="py-3 whitespace-nowrap">{getPriorityBadge(ticket.priority)}</TableCell>
                      <TableCell className="py-3 text-sm">
                        {ticket.assignedResourceName ? (
                          <span className="flex items-center gap-1.5 truncate" title={ticket.assignedResourceName}>
                            <User className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                            <span className="truncate">{ticket.assignedResourceName.split(' ')[0]}</span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground italic">Unassigned</span>
                        )}
                      </TableCell>
                      <TableCell className="py-3 text-right text-sm">
                        {ticketAge !== null && (
                          <span className={`font-semibold ${ticketAge > 7 ? 'text-red-500' : ticketAge > 3 ? 'text-orange-500' : ''}`}>
                            {ticketAge}d
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* No open tickets message */}
      {openTickets.length === 0 && metrics.total > 0 && (
        <Card className="flex-1">
          <CardContent className="flex items-center justify-center h-full py-8">
            <div className="text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-3" />
              <p className="font-medium text-green-700 dark:text-green-400">All tickets resolved!</p>
              <p className="text-sm text-muted-foreground mt-1">
                {metrics.closed} tickets were handled during this period
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Related Recommendations */}
      {relatedRecommendations.length > 0 && (
        <Card className="mt-3">
          <CardHeader className="pb-2 pt-3 px-3">
            <CardTitle className="text-sm font-medium flex items-center gap-1">
              <ArrowRight className="h-4 w-4" />
              Recommendations Addressing These Issues
            </CardTitle>
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="space-y-2">
              {relatedRecommendations.map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-muted/50 rounded text-sm">
                  <div>
                    <span className="font-medium">{item.recommendation.title}</span>
                    <p className="text-xs text-muted-foreground">{item.relevance}</p>
                  </div>
                  <Badge variant="secondary">{item.issue}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ticket Discussion Dialog */}
      <TicketDiscussionDialog
        ticket={selectedTicket}
        open={showDiscussionDialog}
        onOpenChange={setShowDiscussionDialog}
        ticketUrl={selectedTicket ? getTicketUrl(activePsaProvider, serverUrl, instanceUrl, selectedTicket.id) : null}
        hideInternalByDefault
      />
    </div>
  );
}
